/**
 * Viewability tracking for direct-served ads.
 *
 * Uses @neuronic/viewability's trackDuration to measure in-view time
 * and posts delta updates to the exchange's viewability URL.
 *
 * The first ping marks the impression as viewable (first: true).
 * Subsequent pings carry only the delta time (first: false).
 *
 * Transport priority: fetch (keepalive) → sendBeacon → XMLHttpRequest.
 * Restricted traffic explicitly omits credentials and skips sendBeacon,
 * whose credential behavior cannot be controlled by the caller.
 */

import { trackDuration } from '@neuronic/viewability';

/**
 * Posts a viewability event to the exchange. Tries fetch with keepalive
 * first for connection reuse, falls back to sendBeacon, then XHR.
 *
 * @param {string} url
 * @param {boolean} first
 * @param {number} deltaMs
 * @param {boolean} allowCredentials
 */
export function postViewability(url, first, deltaMs, allowCredentials = true) {
  const body = JSON.stringify({ first, delta_ms: Math.round(deltaMs) });

  if (typeof fetch === 'function') {
    try {
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
        credentials: allowCredentials ? 'same-origin' : 'omit',
      }).catch(() => {});
      return;
    } catch (_) {
      // fetch may throw synchronously in some environments (e.g. service worker scope)
    }
  }

  if (allowCredentials
    && typeof navigator !== 'undefined'
    && typeof navigator.sendBeacon === 'function') {
    navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
    return;
  }

  if (typeof XMLHttpRequest !== 'undefined') {
    try {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', url, true);
      if (!allowCredentials) xhr.withCredentials = false;
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.send(body);
    } catch (_) {}
  }
}

/**
 * Starts viewability tracking on a rendered ad wrapper.
 *
 * @param {HTMLElement} wrapper - the rendered ad container element
 * @param {string} viewabilityUrl - exchange viewability postback URL
 * @param {{ allowCredentials?: boolean }} options
 * @returns {() => void} stop function to clean up tracking
 */
export function startViewabilityTracking(wrapper, viewabilityUrl, { allowCredentials = true } = {}) {
  let isFirst = true;

  return trackDuration({
    element: wrapper,
    threshold: 0.5,
    interval: 500,
    onPing: (ping) => {
      postViewability(viewabilityUrl, isFirst, ping.delta, allowCredentials);
      isFirst = false;
    },
    onError: (err) => {
      console.warn('[neuronic] viewability tracking error:', err.message);
    },
  });
}
